import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { library } from '@cwos/api-client';
import { Button, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { LoadFailure } from '../LoadFailure.js';
import { bindingnessLabel, bindingnessVariant, formatDay, ruleLevelLabel } from './libraryLogic.js';
import {
  CONSTRAINED_BY_EXTERNAL,
  EQUAL_STANDING_CONTRADICTION,
  EQUIVALENT_TO_EXTERNAL,
  EXECUTION_ADMISSIBLE_NOTE,
  EXECUTION_BLOCKED_NOTE,
  EXECUTION_ENVELOPE_HEADING,
  FAIL_CLOSED_NOTE,
  NO_SUPPRESSION_NOTE,
  TENANT_POLICY_STRICTER,
  UNDETERMINED_HEADING,
  UNDETERMINED_NOTE,
  anyComputationFailure,
  applicabilityStateLabel,
  applicabilityStateVariant,
  asOfStanding,
  contributingRoleLabel,
  contributingRoleSentence,
  contributingRoleVariant,
  jurisdictionStanding,
  rejectionReasonLabel,
  reportableConflictSentence,
  requirementConflictSentence,
  requirementSentence,
  todayISO,
  undeterminedDimensionSentence,
  versionChainFor,
  versionLabelText,
  versionStateLabel,
} from './applicableLogic.js';

/**
 * WHAT APPLIES HERE, AND WHY — the resolver, made visible.
 *
 * ===========================================================================
 * THIS IS WHERE THE OPERATIONAL LIBRARY BECOMES A PRODUCT
 * ===========================================================================
 * `apps/api/src/domains/intelligence/library/` is two thousand lines of catalog
 * and fail-closed applicability that four other domains derive their vocabulary
 * from, and it had no API and no tool. `GET /knowledge/applicable` is the route
 * that reaches it, and this is the first screen that has ever called it.
 *
 * ===========================================================================
 * THE REJECTIONS ARE HALF OF THE ANSWER
 * ===========================================================================
 * The resolver KEEPS the candidates that did not apply, with the dimension each
 * failed on, so a compliance officer reads "we considered the Minnesota rule
 * and here is the dimension it failed on" instead of a silence. A screen that
 * dropped them would turn a reasoned answer into "you are not subject to
 * anything", which is a compliance answer somebody acts on.
 *
 * ===========================================================================
 * "CURRENT" MUST NOT READ AS "ALWAYS WAS"
 * ===========================================================================
 * The resolver takes an `at` and honours each rule's in-force window against
 * it. A rule that governed in June and was replaced in August is the answer to
 * a June question and not to today's. So the date is a control on this screen,
 * it defaults to today, and the answer states which day it is about — in
 * different words for a past date, so a historical answer can never be read as
 * today's.
 *
 * ===========================================================================
 * NAMING A DIMENSION IS AN AUTHORIZED ACT
 * ===========================================================================
 * `clientId`, `programId`, `serviceId`, `locationId`, `roleId` and `payer` each
 * trigger a reach check server-side, and a caller who may not reach the thing
 * they named gets a 403 describing THEIR OWN reach — never the subject — so the
 * refusal asserts nothing about whether that id exists. This screen therefore
 * offers only the two dimensions that are shared TEXT vocabulary and name
 * nothing in particular: the state or region, and the payer key. Offering a
 * free uuid box would be inviting an operator to probe for existence.
 *
 * ===========================================================================
 * THE STATE-OR-REGION BOX WAS A CONTROL THAT DID NOTHING, AND IT IS GONE
 * ===========================================================================
 * It sent `jurisdiction=...` and the server has not accepted that parameter
 * since founder ruling 1 (2026-09-06) made jurisdiction RESOLVED rather than
 * supplied. `resolveQuerySchema` is a Zod object, so the unknown key was
 * stripped and the request returned 200: an operator could type `US-WI`, get an
 * answer computed for Minnesota, and have nothing on the page tell them so. On
 * a compliance surface that is worse than a 400 — they believe they asked a
 * question they did not ask.
 *
 * (It survived every typecheck because the query object is built with a
 * conditional SPREAD, and TypeScript's excess-property check does not see
 * through one. The field is gone from `library.ResolveQuery` now as well, so
 * the same mistake fails to compile.)
 *
 * What replaces it is not another input. It is the ANSWER to the question the
 * box pretended to ask: `Resolution.jurisdiction` states which jurisdictions
 * the server bound, at what scope and on what basis — or that it could not say,
 * which is a different answer from "nothing applies" and is rendered as one.
 *
 * ===========================================================================
 * THERE IS NO DISMISS, HIDE OR ACKNOWLEDGE ON THIS SCREEN
 * ===========================================================================
 * A tenant may not silently suppress an applicable absolute requirement. That
 * clause of the two-authority ruling is not enforced by a check somewhere — it
 * is enforced by there being no affordance, no route and no column that could
 * do it, and by NO_SUPPRESSION_NOTE saying so on the page rather than leaving
 * the absence to look like an oversight.
 */
export function Applicable() {
  const today = todayISO();
  const [at, setAt] = useState(today);
  const [subject, setSubject] = useState('');
  // Applied, not live: a keystroke must not be a query, and the answer on
  // screen must always match the controls that produced it.
  const [applied, setApplied] = useState<library.ResolveQuery>({ at: today });

  const resolution = useQuery({
    queryKey: ['library', 'applicable', applied],
    queryFn: () => library.resolveApplicable(applied),
  });

  const standing = asOfStanding(applied.at ?? today, today);

  return (
    <div className="rn-col">
      <form
        className="rn-card rn-col"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({
            at,
            ...(subject.trim() === '' ? {} : { subject: subject.trim() }),
          });
        }}
      >
        <h2 className="rn-card-title">Ask about a situation</h2>
        <div className="rn-row">
          <label className="rn-col">
            <span className="rn-strong">As of</span>
            <input
              type="date"
              value={at}
              onChange={(e) => setAt(e.target.value)}
              aria-label="As of"
            />
          </label>
          <label className="rn-col">
            <span className="rn-strong">Question (optional)</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              aria-label="Question"
            />
            <span className="rn-muted">Leave empty for every question you have an answer to.</span>
          </label>
        </div>
        <p className="rn-muted">{FAIL_CLOSED_NOTE}</p>
        <Button type="submit" variant="primary">
          Work it out
        </Button>
      </form>

      <p className="rn-muted" role="note">
        Asking about {formatDay(applied.at ?? today)}. {standing.sentence}
      </p>

      {resolution.isLoading ? <SkeletonSet surface="drawer" rows={5} /> : null}

      {resolution.isError ? (
        <LoadFailure
          error={resolution.error}
          message="We couldn’t work this out just now. This is not a statement that nothing applies to you."
          onRetry={() => void resolution.refetch()}
          // A 403 here is about the READER'S reach, never about whether the
          // thing they named exists. The heading says so.
          refusedHeading="This question is outside what your account may ask about"
        />
      ) : null}

      {resolution.data ? <Answer resolution={resolution.data} /> : null}
    </div>
  );
}

function Answer({ resolution }: { resolution: library.Resolution }) {
  const { subjects, rejected, externalVersions, undeterminedApplicability } = resolution;
  const computationFailed = anyComputationFailure(rejected);
  const where = jurisdictionStanding(resolution.jurisdiction);
  // AN OUTSIDE REQUIREMENT ACTUALLY GOVERNS THIS ANSWER. Read off the roles the
  // resolver assigned, not off `externalVersions` (a chain is history and a
  // historical question cites an old version) and not off `constrainedByExternal`
  // (a floor that clamps nothing is still a floor). It decides whether the
  // "this cannot be dismissed" sentence is a statement about this answer or a
  // sentence about nothing.
  const hasExternalFloor = subjects.some((s) =>
    s.contributing.some((c) => c.role === 'external_floor'),
  );

  return (
    <div className="rn-col">
      {/*
        WHICH STATES THIS WAS WORKED OUT FOR, AND WHETHER IT COULD BE.

        FIRST ON THE PAGE, ABOVE THE ANSWER, because it qualifies everything
        below it. An external hard requirement is matched on jurisdiction, so a
        resolver that could not bind one applied no floor at all — and the
        answer it produced looks exactly like an answer where no floor exists.
        `no_jurisdiction_recorded` is an ALERT rather than a note for that
        reason: it is the four-state collapse arriving as a perfectly ordinary
        looking page.
      */}
      <section className="rn-card">
        <h2 className="rn-card-title">{where.heading}</h2>
        <p
          className={
            where.kind === 'answered' || where.kind === 'widened' ? 'rn-muted' : 'rn-strong'
          }
          role={where.kind === 'answered' || where.kind === 'widened' ? 'note' : 'alert'}
        >
          {where.sentence}
        </p>
        {where.jurisdictions.length > 0 ? (
          <p className="rn-row">
            {where.jurisdictions.map((j) => (
              <Pill key={j} variant="sky">
                {j}
              </Pill>
            ))}
          </p>
        ) : null}
      </section>

      {computationFailed ? (
        // NOTHING WAS WORKED OUT. This is the one "rejection" that is not a
        // decision, and grouping it with the mismatches below would let a
        // failure to ask be read as "no rules apply".
        <p className="rn-strong" role="alert">
          Some candidates were set aside because a date could not be read, so they were never
          weighed. That is not a decision that they do not apply to you — see the reasons below.
        </p>
      ) : null}

      {subjects.length === 0 ? (
        <SuccessEmpty
          heading="Nothing your organization has decided answers this"
          message={
            rejected.length === 0
              ? 'No rule in your Library and no outside requirement speaks to this. Phibian goes on what it can see in your records instead.'
              : 'Rules were considered and none of them applied to the situation you described. What they were, and the dimension each failed on, is below.'
          }
        />
      ) : null}

      {subjects.map((s) => (
        <section className="rn-card" key={s.subject}>
          <h2 className="rn-card-title">{s.subject}</h2>
          <p className="rn-strong">{requirementSentence(s.effective)}</p>

          {/*
            SENTENCE ONE, IN THE SERVER'S OWN WORDS AND NOT RE-DERIVED HERE.

            `requirementSentence` above renders the machine-comparable
            requirement ("at least 120 minutes per month"). This is the
            governing requirement AS A SENTENCE, with the authority that issued
            it — and it is the half that must stay legible when the execution
            envelope below says Phibian will not act. The two are composed by
            two server functions that cannot see each other's inputs
            (`library/admissibility.ts`), so a merged explanation is not
            something this screen could produce even by laying them out badly.

            UNTRUSTED TEXT. It quotes a rule statement, so it goes through a
            JSX text node like everything else on this screen.
          */}
          <p className="rn-muted">{s.governingSentence}</p>

          {s.constrainedByExternal ? (
            <p className="rn-strong" role="note">
              {CONSTRAINED_BY_EXTERNAL}
            </p>
          ) : null}

          {/*
            YOUR POLICY IS THE STRICTER ONE, SAID OUT LOUD. Without it, "an
            outside requirement applies to you" sits on the screen with no
            statement of what it did, and a reader assumes it overrode them.
          */}
          {s.floorComparison === 'tenant_policy_stricter' ? (
            <p className="rn-strong" role="note">
              {TENANT_POLICY_STRICTER}
            </p>
          ) : null}

          {/*
            EXACTLY EQUAL — ITS OWN ANSWER, not a quiet member of "not
            clamped". An agency sitting precisely on a statutory floor has no
            margin, and that is a thing to know before revising the policy down.
          */}
          {s.floorComparison === 'equivalent' ? (
            <p className="rn-strong" role="note">
              {EQUIVALENT_TO_EXTERNAL}
            </p>
          ) : null}

          {s.conflict ? (
            // NAMED, NOT HIDDEN, AND NAMED SPECIFICALLY. Two applicable rules
            // that could not be compared is a real state of the organization's
            // policy, and WHICH WAY they could not be compared decides who has
            // to do something about it: a units disagreement is a decision for
            // a human, an unreadable record is a gap in the Library.
            <p className="rn-strong" role="alert">
              {requirementConflictSentence(s.conflict)}
            </p>
          ) : null}

          {/*
            THE POLICY THAT CANNOT STAND — A DIFFERENT FACT FROM THE CLAMP.

            `constrainedByExternal` above says a number came out higher. This
            says the organization's own approved policy states the OPPOSITE of
            something it cannot opt out of, so the policy has no effect at all.
            An operator not told this concludes the software is ignoring their
            SOP. `role="alert"` and not a muted note: it is the one thing on
            this screen that names a document somebody has to go and change.
          */}
          {s.reportable.map((r) => (
            <p className="rn-strong" role="alert" key={`${r.policyRuleId}:${r.displacedByRuleId}`}>
              {reportableConflictSentence(r.reason)}
            </p>
          ))}

          {/*
            EQUAL STANDING, AND PHIBIAN WILL NOT PICK. Neither rule outranks the
            other, so there is no principled answer and choosing quietly would
            be the software settling something no human has.
          */}
          {s.contradictions.length > 0 ? (
            <p className="rn-strong" role="alert">
              {EQUAL_STANDING_CONTRADICTION}
            </p>
          ) : null}

          {/*
            ---------------------------------------------------------------
            THE OTHER QUESTION. EVERYTHING ABOVE IS ABOUT THE ORGANIZATION;
            EVERYTHING HERE IS ABOUT PHIBIAN.
            ---------------------------------------------------------------
            Platform-constraint ruling of 2026-09-07. A platform constraint may
            stop Phibian acting even where the governing requirement says the
            organization should act, and the explanation must distinguish the
            two. So this is its own block with its own heading rather than one
            more note in the stack above — and the governing sentence is NOT
            suppressed when this block is present, which is the failure the
            ruling is actually about.

            NO PILL, NO RANK, NO ROLE. An execution constraint is not an
            authority and is not styled like one: giving it the visual
            vocabulary of an `external_floor` would tell an operator that
            Phibian's refusal is somebody's law.
          */}
          {s.executionConstraints.length > 0 ? (
            <>
              <h3>{EXECUTION_ENVELOPE_HEADING}</h3>
              <p
                className={s.executionAdmissibility === 'blocked' ? 'rn-strong' : 'rn-muted'}
                role={s.executionAdmissibility === 'blocked' ? 'alert' : 'note'}
              >
                {s.executionAdmissibility === 'blocked'
                  ? EXECUTION_BLOCKED_NOTE
                  : EXECUTION_ADMISSIBLE_NOTE}
              </p>
              <ul className="rn-list">
                {s.executionConstraints.map((c) => (
                  <li key={c.ruleId}>
                    {/* The server's whole sentence, not a paraphrase: its first
                        clause is what stops this reading as external law. */}
                    <p className="rn-muted">{c.sentence}</p>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          <h3>How Phibian got there</h3>
          <ol className="rn-list">
            {s.contributing.map((c) => (
              <li key={c.id}>
                <Pill variant={contributingRoleVariant(c.role)}>
                  {contributingRoleLabel(c.role)}
                </Pill>{' '}
                <Pill variant={bindingnessVariant(c.bindingness)}>
                  {bindingnessLabel(c.bindingness)}
                </Pill>{' '}
                <span className="rn-muted">{ruleLevelLabel(c.ruleLevel)}</span>
                {/*
                  THE STATEMENT IS NEVER WITHHELD and it is UNTRUSTED TEXT.
                  It came out of a document somebody uploaded, so it is rendered
                  as characters into a paragraph — no markup, no interpretation
                  — and it is the organization's own policy, which its people
                  are entitled to read.

                  THE SAME IS TRUE OF AN OUTSIDE REQUIREMENT, and more sharply:
                  `global_knowledge_source.source_text` is INGESTED text from
                  somewhere else entirely. It goes through the same JSX text
                  node. Nothing on this screen calls `dangerouslySetInnerHTML`
                  and `applicable-external-floor.test.tsx` scans this directory
                  to keep it that way — a render assertion alone would go green
                  the moment somebody added a hatch to a panel that happens not
                  to be mounted.
                */}
                <p className="lib-body">{c.statement}</p>
                <p className="rn-muted">{contributingRoleSentence(c.role)}</p>
                <p className="rn-muted">{requirementSentence(c.requirement)}</p>
                <EffectiveWindow from={c.effectiveFrom} to={c.effectiveTo} />
                <Source provenance={c.provenance} withheld={c.provenanceWithheld} />
                {c.displacedBy ? (
                  // WHICH RULE WON, NAMED. "This lost" without "to what" is
                  // half an explanation, and the resolver knows the other half.
                  <p className="rn-muted">
                    Outranked here by{' '}
                    {c.displacedBy.scope === 'global' ? 'an outside requirement' : 'your own rule'}
                    {c.displacedBy.version === null ? '' : ` (version ${c.displacedBy.version})`}.
                  </p>
                ) : null}
                {c.scope === 'global' ? (
                  <VersionHistory versions={externalVersions} citedRuleId={c.id} />
                ) : null}
              </li>
            ))}
          </ol>
        </section>
      ))}

      {/*
        WHY THERE IS NO WAY TO MAKE ANY OF THIS GO AWAY.

        Shown only when an outside requirement actually governs something in
        this answer — a standing sentence on a page with no external floor in it
        would be answering a question nobody asked. See NO_SUPPRESSION_NOTE for
        why the absence of a dismiss control is a decision and not a gap.
      */}
      {hasExternalFloor ? (
        <p className="rn-muted" role="note">
          {NO_SUPPRESSION_NOTE}
        </p>
      ) : null}

      {/*
        THE ROUTED GAP, ABOVE THE PILE AND NOT IN IT.

        "If Phibian cannot establish that a regulatory requirement governs the
        relevant context, it must not present the requirement as settled legal
        truth. It may surface 'Applicability could not be determined because
        provider/program/service classification is incomplete' and ROUTE THAT
        GAP for management/compliance resolution." (founder, 2026-09-08)

        Every row here is also below in "Considered and set aside", with its
        reasons — nothing is hidden either way. It is here as well because
        ROUTED is a stronger word than not-omitted: an operator scanning a list
        of correct non-applications for the two entries that mean "we could not
        answer" will not find them, and the answer they walk away with is
        "nothing applies". `role="alert"` for the same reason
        `no_jurisdiction_recorded` is an alert and not a note.
      */}
      {undeterminedApplicability.length > 0 ? (
        <section className="rn-card" role="alert">
          <h2 className="rn-card-title">{UNDETERMINED_HEADING}</h2>
          <p className="rn-strong">{UNDETERMINED_NOTE}</p>
          <ul className="rn-list">
            {undeterminedApplicability.map((u) => (
              <li key={`${u.subject}:${u.ruleId}`}>
                <span className="rn-strong">{u.subject}</span>{' '}
                <span className="rn-muted">
                  ({u.scope === 'global' ? 'an outside requirement' : 'your own policy'})
                </span>
                <ul>
                  {u.undetermined.map((d) => (
                    <li key={`${d.dimension}:${d.reason}`} className="rn-muted">
                      {undeterminedDimensionSentence(d.dimension, d.reason)}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="rn-card">
        <h2 className="rn-card-title">Considered and set aside</h2>
        {rejected.length === 0 ? (
          <p className="rn-muted">
            Nothing was set aside. Every rule Phibian looked at applied to the situation you
            described.
          </p>
        ) : (
          <>
            <p className="rn-muted">
              These rules exist and none of them is in the answer above. Each says which — it does
              not apply to you, it is not yet in effect, it is no longer in effect, or Phibian could
              not determine whether it governs you. Only the first is a decision.
            </p>
            <ul className="rn-list">
              {rejected.map((r) => (
                <li key={r.id}>
                  <span className="rn-strong">{r.subject}</span>{' '}
                  <span className="rn-muted">
                    ({r.scope === 'global' ? 'an outside requirement' : 'your own policy'})
                  </span>{' '}
                  {/*
                    WHICH ANSWER THIS IS, before the reasons that produced it.
                    "Set aside" is four different facts and only one of them —
                    `does_not_apply` — is a decision somebody may act on.
                  */}
                  <Pill variant={applicabilityStateVariant(r.state)}>
                    {applicabilityStateLabel(r.state)}
                  </Pill>
                  <ul>
                    {r.reasons.map((reason) => (
                      <li key={reason} className="rn-muted">
                        {rejectionReasonLabel(reason)}
                      </li>
                    ))}
                  </ul>
                  <Source provenance={r.provenance} withheld={r.provenanceWithheld} />
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}

/**
 * Where a rule came from.
 *
 * WITHHELD IS READ BEFORE THE NULL, always. `provenance` is `null` exactly when
 * `provenanceWithheld` is true, and the flag is the only thing that can tell
 * "we have it and you may not see it" apart from "there is none" — for a tenant
 * rule the second is impossible, because the source foreign key is NOT NULL and
 * ON DELETE RESTRICT.
 */
function Source({
  provenance,
  withheld,
}: {
  provenance: library.Provenance | null;
  withheld: boolean;
}) {
  if (withheld) {
    return (
      <p className="rn-muted">
        Where this came from is not shown to you. The document is outside your programs — the rule
        is not being withheld, its source is.
      </p>
    );
  }
  if (provenance === null) {
    return <p className="rn-muted">Phibian has no source recorded for this.</p>;
  }
  return (
    <p className="rn-muted">
      From {provenance.sourceTitle}
      {provenance.versionLabel ? ` (${provenance.versionLabel})` : ''}
      {provenance.approvedAt ? ` · approved ${formatDay(provenance.approvedAt.slice(0, 10))}` : ''}
      {provenance.effectiveFrom ? ` · in force from ${formatDay(provenance.effectiveFrom)}` : ''}
      {provenance.effectiveTo ? ` to ${formatDay(provenance.effectiveTo)}` : ''}
      {provenance.approvedAt ? '' : ' · this one was not approved by anyone here'}
    </p>
  );
}

/**
 * The window a rule was in force for, as it stands now.
 *
 * ABSENT IS SAID OUT LOUD. A missing `effectiveFrom` is a rule whose start
 * nobody recorded — which happens for external material where the publisher
 * gave no date — and rendering nothing would let a reader assume it has always
 * been in force. An open `effectiveTo` is a genuinely open window and says so.
 */
function EffectiveWindow({ from, to }: { from: string | null; to: string | null }) {
  return (
    <p className="rn-muted">
      {from === null ? 'No start date is recorded for this' : `In force from ${formatDay(from)}`}
      {to === null ? ', with no end date' : ` to ${formatDay(to)}`}.
    </p>
  );
}

/**
 * VERSION AND SUPERSESSION HISTORY for an outside requirement.
 *
 * ===========================================================================
 * WHY THIS CANNOT BE READ OUT OF THE ANSWER
 * ===========================================================================
 * The resolver returns what was in force at the instant asked about. A version
 * replaced two years ago is therefore CORRECTLY absent from a question about
 * today — so a screen built only on `contributing` can say what the requirement
 * is and can never say that it replaced something. `Resolution.externalVersions`
 * carries the chain separately, and {@link versionChainFor} walks it in both
 * directions from the version actually cited.
 *
 * ===========================================================================
 * WHAT THE CHAIN IS FOR, AND IT IS NOT DECORATION
 * ===========================================================================
 * An approved external requirement is IMMUTABLE: a material change is a new
 * version that supersedes, never an edit, "so that anything that cited the
 * prior requirement must still resolve to the exact version that governed it —
 * a regulation update must not silently rewrite historical evidence". A tenant
 * that can see only the current version has no way to check that claim. This is
 * where they check it.
 *
 * A requirement with no predecessor and no successor renders NOTHING rather
 * than a one-row "history" — a chain of one is not a history, and printing it
 * would make a corpus that has never been revised look as though it had.
 */
function VersionHistory({
  versions,
  citedRuleId,
}: {
  versions: readonly library.ExternalRequirementVersion[];
  citedRuleId: string;
}) {
  const chain = versionChainFor(versions, citedRuleId);
  if (chain.length < 2) return null;

  return (
    <div className="rn-col">
      <h4>Earlier and later versions of this requirement</h4>
      <p className="rn-muted">
        An outside requirement is never edited. A change is published as a new version that replaces
        the old one, and the old one is kept exactly as it stood — so a decision made under it can
        still be checked against the words that were in force at the time.
      </p>
      <ol className="rn-list">
        {chain.map(({ version, isCited }) => (
          <li key={version.id}>
            <Pill variant={isCited ? 'good' : 'plain'}>
              {isCited ? 'The version in this answer' : versionStateLabel(version.state)}
            </Pill>{' '}
            <span className="rn-strong">{versionLabelText(version.versionLabel)}</span>
            <EffectiveWindow from={version.effectiveFrom} to={version.effectiveTo} />
            {/*
              THE PUBLISHED TEXT OF A PAST VERSION, AS CHARACTERS. Ingested from
              outside this product and rendered into a text node — see the note
              on `statement` above. Kept, and not summarised, because "the exact
              version that governed it" is a claim about words.
            */}
            <p className="lib-body">{version.statement}</p>
            <p className="rn-muted">
              {version.publisher}
              {version.citation === null ? '' : ` · ${version.citation}`}
              {version.jurisdiction === null ? '' : ` · ${version.jurisdiction}`}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
